import { createSelector } from 'reselect';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import values from 'lodash/values';
import uniq from 'lodash/uniq';
import type { QuestionType, QnAStateType, ConditionsType } from '../index';
import { updateFollowUp } from '../../utils/compat';
import { isSelected } from '../../utils/answer';
import { getQuestionById, getQuestions, getTopQuestionIds } from './questions';
import { getUserAnswers } from './answers';
import type { UserAnswerMapT } from '../answers';
const questionToOption = question => ({
  value: question.id,
  label: question.question
});

// Warning: options being mutated
const getFollowUpQuestionsAsOptions = (options, state, question, targetQuestionId) => {
  let targetQuestionReached = false;
  const followUps = get(question, 'followUps') || [];
  if (isEmpty(followUps)) return targetQuestionReached;

  // using lodash foreach to short-circuit loop
  forEach(followUps, followUp => {
    const newFollowUp = updateFollowUp(followUp);
    const questionIds = get(newFollowUp, 'questionIds');
    forEach(questionIds, questionId => {
      if (targetQuestionId === questionId) {
        targetQuestionReached = true;
        // break out forEach
        return false;
      }
      const followUpQuestion = getQuestionById(state, questionId);
      options.push(questionToOption(followUpQuestion));
      targetQuestionReached = getFollowUpQuestionsAsOptions(options, state, followUpQuestion, targetQuestionId);
      // short circuit
      if (targetQuestionReached) {
        // break out forEach
        return false;
      }
    });

    // short circuit
    if (targetQuestionReached) {
      // break out forEach
      return false;
    }
  });
  return targetQuestionReached;
};
export const getQuestionsAsOptionsForCertainQuestion = (state: QnAStateType, targetQuestionId: string, topQuestionIds: Array<string>) => {
  let options = [];
  forEach(topQuestionIds, questionId => {
    const question = getQuestionById(state, questionId);
    if (question) {
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type '{ value: any; label: any; }' is not assignable to parameter of type 'never'.
      options.push(questionToOption(question));
      const targetQuestionReached = getFollowUpQuestionsAsOptions(options, state, question, targetQuestionId);
      if (targetQuestionReached) {
        return false;
      }
    }
  });
  return options;
};
export const possibleQuestionForFollowUpConditionSelector = createSelector(state => state, (_, targetQuestionId) => targetQuestionId, getTopQuestionIds, getQuestionsAsOptionsForCertainQuestion);
export const getDescendentFollowUpQuestionIds = (state: QnAStateType, questionId: string) => {
  const question = getQuestionById(state, questionId);
  const followUps = get(question, 'followUps') || [];
  return followUps.reduce((prev, followUp) => {
    const newFollowUp = updateFollowUp(followUp);
    // adding followup question ids
    prev.push(...newFollowUp.questionIds);

    // adding follow up question's follow up ids
    newFollowUp.questionIds.forEach(id => {
      prev = prev.concat(getDescendentFollowUpQuestionIds(state, id));
    });
    return prev;
  }, []);
};
export const getAllDescendentFollowUpQuestionIds = createSelector((state: QnAStateType) => state, (_, questionId: string) => questionId, getDescendentFollowUpQuestionIds);
export const getQuestionsAsOptions = (state: QnAStateType) => {
  const questions = values(getQuestions(state)) || [];
  // @ts-expect-error todo(ts-migration) TS2769 No overload matches this call.
  const options = questions.map(q => omit(q, ['followUps']));
  return options;
};
export const matchConditionWithAnswers = (conditions: ConditionsType, userAnswersMap: UserAnswerMapT, question?: QuestionType) => {
  if (isEmpty(conditions)) {
    return false;
  }
  let matched = false;
  // OR
  forEach(conditions, ANDs => {
    // AND
    let resultForAnd = true;
    forEach(ANDs, AND => {
      const answersToQuestion = get(userAnswersMap, [AND.id, 'answers']) || [];
      if (isEmpty(answersToQuestion) || !isSelected(answersToQuestion, AND.answer, AND.operator, question)) {
        resultForAnd = false;
      }
      if (!resultForAnd) {
        // break forEach
        return false;
      }
    });
    if (resultForAnd) {
      matched = true;
      // break forEach
      return false;
    }
  });
  return matched;
};
export const getFollowUpQuestionIdsByAnswers = (state: QnAStateType, questionId: string): string[] => {
  const question = getQuestionById(state, questionId);
  const followUps = get(question, 'followUps') || [];
  const userAnswersMap = getUserAnswers(state);
  const followUpQuestionIds = uniq(followUps.reduce((prev, oldFollowUp) => {
    if (matchConditionWithAnswers(oldFollowUp.conditions, userAnswersMap, question)) {
      // backward compat
      const followUp = updateFollowUp(oldFollowUp);
      return prev.concat(followUp.questionIds);
    }
    return prev;
  }, []));

  // @ts-expect-error todo(ts-migration) TS2322 Type 'unknown[]' is not assignable to type 'string[]'.
  return followUpQuestionIds;
};